import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
  type Theme,
} from "@mui/material";
import dayjs from "dayjs";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  fetchSlaCompliance,
  fetchSlaDrilldown,
  fetchSlaModelBreakdown,
  fetchSlaTrend,
  isSlaFixtureMode,
  processmodelsApi,
  toSlaApiError,
  type SlaApiError,
} from "api";
import { getTranslation } from "common";
import { DateRangePickerValue } from "components/DateRangePickerMenu";
import { useBreakpoint, useTranslations, useUser } from "hooks";
import {
  SLA_STATISTICS_BASES,
  SLA_STATISTICS_DIMENSION,
  SLA_STATISTICS_EXCLUDED_OUTCOMES,
  SLA_STATISTICS_GRANULARITIES,
  SLA_STATISTICS_GRANULARITY_DEFAULT,
  SLA_STATISTICS_LEVELS,
  SLA_STATISTICS_LEVEL_DEFAULT,
  SLA_STATISTICS_ROW_IDENTITY,
  SLA_STATISTICS_STALE_SOURCE,
  SLA_STATISTICS_TEXT_KEYS,
  slaStatisticsBucketRange,
  slaStatisticsCustomerValues,
  slaStatisticsDrilldownQuery,
  slaStatisticsDrilldownTitleKey,
  slaStatisticsFormatWorkingSeconds,
  slaStatisticsGranularityKey,
  slaStatisticsHasStatisticsKey,
  slaStatisticsLevelKey,
  slaStatisticsMessageKey,
  slaStatisticsNullableRate,
  slaStatisticsPeriod,
  slaStatisticsPeriodDays,
  slaStatisticsPeriodWithinLimit,
  slaStatisticsQuery,
  slaStatisticsRate,
  slaStatisticsSample,
  slaStatisticsTrendQuery,
  slaStatisticsWorkingDuration,
  type SlaStatisticsBasis,
  type SlaStatisticsRange,
} from "model/slaStatisticsModel";
import { slaStatisticsExportFilterSummary } from "model/slaStatisticsExportModel";
import { SLA_MONITOR_TEXT_KEYS, isSlaMonitorStale } from "model/slaMonitorModel";
import type {
  SlaComplianceBase,
  SlaComplianceLevel,
  SlaComplianceResponse,
  SlaComplianceSample,
  SlaDrilldownResponse,
  SlaModelBreakdownResponse,
  SlaModelBreakdownRow,
  SlaStatisticsGranularity,
  SlaTrendPoint,
  SlaTrendResponse,
} from "model/slaModel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SlaStatisticsExportMenu from "./SlaStatisticsExportMenu";
import { useSlaRuntimeRefresh } from "./useSlaRuntimeRefresh";
import useSlaStatisticsText, { SLA_STATISTICS_UI_TEXT_KEYS } from "./useSlaStatisticsText";

/**
 * Superficie **Statistiche SLA** (piano Client sez. 5 F4a e F4b, sez. 6 riga 90,
 * sez. 8 riga 128).
 *
 * **Dove e' innestata**: nella pagina statistiche **esistente**
 * (`src/pages/Statistics.tsx`), come terza vista accanto a "performance" e
 * "Gantt" — nessuna pagina parallela (sez. 3, stesso criterio di F3c). Il
 * comando di vista usa la chiave `sla.statistics.title`; l'intervallo di date
 * della pagina (`DateRangePickerMenu`, persistito in `localStorage`) e il
 * selettore di modello **sono** i filtri della superficie: non nasce un secondo
 * periodo che potrebbe divergere dalla pagina.
 *
 * Cosa rispetta dei criteri F4a:
 * - **due compliance chiaramente etichettate**: due blocchi distinti
 *   (`sla-statistics-basis-original` / `-effective`), ognuno con la propria
 *   label del catalogo, il proprio `data-basis` e i propri valori del payload.
 *   Nessuna base e' calcolata dall'altra e le due non sono intercambiabili;
 * - **denominatori separati**: in ogni blocco il denominatore (`eligible`) e'
 *   reso con la sua etichetta (`sla.statistics.denominator`) accanto alla
 *   percentuale, e i tre esiti esclusi (annullati 2, interrotti 3, chiusi
 *   d'autorita' 4) stanno in un blocco **fratello** (`sla-statistics-excluded`),
 *   mai sommati al denominatore — un processo interrotto non e' un completamento
 *   valido. `without_sla` e' un terzo blocco separato;
 * - **N/D con zero campione**: `percent`, `coverage_percent` e
 *   `average_working_seconds` nulli rendono la chiave "non disponibile"
 *   (`data-available="false"`), **mai** `0%`;
 * - **periodo/timezone e filtri coerenti**: il periodo `[from, to)` e la
 *   timezone resi sono quelli che la superficie dichiara e invia (il payload P2
 *   li restituisce nella stessa forma); cambiare periodo o livello rilegge la
 *   conformita'; oltre il limite di 399 giorni del contratto la superficie
 *   **dichiara** il limite e non invia una richiesta che il Server rifiuterebbe;
 * - **nessuna metrica per persona**: nessuna riga, colonna o insight porta
 *   utente, ruolo o livello di escalation; la scelta e' dichiarata dalla
 *   superficie (`data-dimension`) e resa con la nota di riservatezza del
 *   payload.
 *
 * Cosa aggiunge **F4b** (stesso contratto reale, `schemas/sla.py:117-181`):
 * - **trend**: sezione `sla-statistics-trend` con la granularita' giorno/settimana
 *   (due chiavi P1) e un punto per bucket; ogni punto porta **entrambe** le basi
 *   (`data-original-*` / `data-effective-*`) e la sua identita' e' il `bucket`
 *   del payload, quindi rileggere la serie rende gli **stessi** punti: nessuna
 *   identita' viene rigenerata dall'indice;
 * - **drill-down**: si apre **dal punto aggregato** (il punto e' un bottone con
 *   `aria-pressed`; Escape richiude) e mostra le righe del contratto con
 *   identita' `sla_instance_id` (`SLA_STATISTICS_ROW_IDENTITY`,
 *   `sla_result.idinstance` **UNIQUE**): la riga e' il risultato finale di
 *   un'istanza, non un evento per livello, quindi **nessun doppio conteggio** di
 *   un breach escalato. La nota `drilldown_no_double_count` del payload lo dice
 *   all'utente. Nessuna riga porta utente o ruolo;
 * - **`sample_size` visibile** in tutte e tre le viste (`sla.statistics.sample_size`
 *   col `sample` del payload: `rows`, il tetto quando c'e' e `limited`);
 * - **filtro Cliente solo se `isStatisticsKey`**: il flag vive nel `userschema`
 *   del modello (`plans/SLA_Implementation_Plan.md:208-211`); il modello scelto
 *   viene letto una volta (`processmodelsApi`, endpoint gia' usato dall'editor
 *   BPMN) e il filtro compare **solo** se esiste un campo flaggato. Altrimenti la
 *   superficie rende la **guida** del catalogo al posto del filtro
 *   (`sla.statistics.filter.customer_disabled`): mai un comando inerte, e
 *   `customervalues` non viene inviato (`queries.py:100-106`: P2 non risolve il
 *   flag per tenant);
 * - **target del dato visibile**: `data-organization` dichiara l'organizzazione di
 *   cui la metrica parla e il livello contato resta esplicito (`data-level` e
 *   filtro). Un **obiettivo** di organizzazione non esiste nel contratto P2 ne'
 *   nel catalogo P1: non viene inventato (vedi il modello).
 *
 * Cosa aggiunge **F4c** (piano §5 F4c, §8 riga 128, §6 riga 90):
 * - **export PDF/Excel** (`SlaStatisticsExportMenu`): il documento e' un artefatto
 *   del client costruito dai payload **correnti** e dai filtri **correnti**, senza
 *   una seconda richiesta al download, quindi non esiste un percorso in cui l'export
 *   diverta da cio' che l'utente vede (piano §5 F4b). Gli errori sono dichiarati,
 *   non silenziosi (`data-export-error`), e il comando **non** e' disabilitato:
 *   spiega. Vedi `model/slaStatisticsExportModel.ts` per le 20 chiavi `sla.export.*`
 *   del catalogo P1 (renderer `client`) e per il motivo per cui l'esportazione
 *   nasce nel client;
 * - la **disattivazione di organizzazione** (`SlaOrganizationSettingsPanel`) e le
 *   **preferenze individuali** (`SlaUserPreferencesPanel`, riepilogo giornaliero e
 *   saluto) non stanno piu' qui: sono impostazioni, non statistiche, e vivono nella
 *   vista impostazioni dell'organizzazione
 *   (`/dashboard/organizations/:organizationId/settings`, `OrganizationSettings.tsx`).
 *
 * Cosa **non** fa (dichiarato e non simulato): **insight AI** e **allegato email**.
 * Il contratto non ha nessuna delle due rotte — l'insight pianificato
 * (`GET /sla/{org}/insights`, `Flowenti-Server/plans/SLA_Implementation_Plan.md:280`)
 * non e' nel controller Python (`backend_statistiche/app/controllers/sla.py:166,197,232,260`:
 * solo compliance, trend e due drill-down) e la tabella cache `ai_sla_suggestions`
 * non esiste nelle migrazioni (`TestServer/data/SCRIPT/`); l'invio email e' fuori dal
 * repository Python (`app/tests/test_sla_export.py:14-18`). Nessun comando inerte
 * viene offerto al posto loro: il test lo asserisce.
 *
 * Impaginazione (una sola pagina, nessuna scheda): barra compatta (livello,
 * Cliente, export a destra) con una riga di didascalia (periodo, timezone,
 * aggiornamento localizzato); schede KPI (effettiva, originale, campione,
 * copertura, tempo medio leggibile); grafico del trend (recharts, una scala
 * 0-100%, effettiva = primario, originale = neutro tratteggiato) con la tabella
 * per periodo come vista testuale: ogni riga e' un punto (`bucket`) e apre il
 * dettaglio delle istanze in tabella. Gli esiti esclusi stanno in **una** riga e
 * la nota di riservatezza **una** volta, in fondo. Il contratto P2 non ha un
 * aggregato per processo con nome (solo righe per istanza paginate), quindi la
 * tabella di ripartizione e' per periodo, non per processo.
 *
 * Responsive (sez. 6 riga 90): le schede usano una griglia che va a capo, le
 * tabelle scorrono nel proprio contenitore; nessuno scroll orizzontale della
 * pagina e nessun comando vive solo su hover (il grafico ha la tabella accanto).
 *
 * Freschezza (sez. 5 F3d): `as_of` del payload confrontato con l'orologio che
 * avanza; la superficie rilegge al rientro sul tab con il meccanismo condiviso
 * (`useSlaRuntimeRefresh`) e con dati scaduti lo dichiara (`data-stale`), invece
 * di mostrare una metrica falsamente fresca.
 */
interface SlaStatisticsSurfaceProps {
  idorganization: number;
  /** Intervallo della pagina statistiche (stessa fonte, nessun secondo periodo). */
  interval: DateRangePickerValue;
  /** Modello selezionato nella pagina (filtro `modelids` del contratto P2). */
  modelId?: number | null;
}

/** Prefisso dei `data-testid`: il contratto DOM della superficie e' uno solo. */
/**
 * Le etichette ordinabili sono ButtonBase (`user-select: none`): senza questo
 * override copiare la tabella perde le intestazioni e i nomi finiscono
 * incollati al testo vicino.
 */
const SORT_LABEL_SX = { userSelect: "text" } as const;

const TEST_ID = "sla-statistics";

/** Stato del filtro Cliente: dipende dal `userschema` del modello scelto. */
type CustomerFilterState = "available" | "blocked" | "no_model";

/**
 * Bersaglio del drill-down: periodo (bucket del trend o periodo intero) e,
 * dalla tabella "Per processo", il modello a cui restringere le istanze.
 */
type DrilldownTarget = SlaStatisticsRange & {
  bucket: string | null;
  processModelId?: number | null;
  processModelName?: string | null;
};

/** Colonne ordinabili della tabella "Per processo". */
type BreakdownSortColumn = "name" | "eligible" | "effective";

const BREAKDOWN_ROWS_PER_PAGE = 15;

const SlaStatisticsSurface = ({
  idorganization,
  interval,
  modelId,
}: SlaStatisticsSurfaceProps) => {
  const translations = useTranslations();
  const user = useUser();
  const theme = useTheme();
  const isCompact = useBreakpoint("down", "md");
  const locale = user?.i18n || navigator.language.split("-")[0] || "en";

  const [level, setLevel] = useState<SlaComplianceLevel>(SLA_STATISTICS_LEVEL_DEFAULT);
  const [granularity, setGranularity] = useState<SlaStatisticsGranularity>(
    SLA_STATISTICS_GRANULARITY_DEFAULT,
  );
  const [data, setData] = useState<SlaComplianceResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<SlaApiError | null>(null);
  const [trend, setTrend] = useState<SlaTrendResponse | null>(null);
  const [trendLoading, setTrendLoading] = useState<boolean>(false);
  const [trendError, setTrendError] = useState<SlaApiError | null>(null);
  /** Ripartizione per modello di processo (`/breakdown/model`), stessi filtri della conformita'. */
  const [breakdown, setBreakdown] = useState<SlaModelBreakdownResponse | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState<boolean>(false);
  const [breakdownError, setBreakdownError] = useState<SlaApiError | null>(null);
  /**
   * Drill-down aperto: periodo (bucket del punto, o periodo intero) selezionato;
   * `processModelId` restringe il dettaglio al modello scelto nella tabella
   * "Per processo" (il filtro modello della barra resta quello generale).
   */
  const [drilldownTarget, setDrilldownTarget] = useState<DrilldownTarget | null>(null);
  const [drilldownPage, setDrilldownPage] = useState<number>(1);
  const [drilldown, setDrilldown] = useState<SlaDrilldownResponse | null>(null);
  const [drilldownLoading, setDrilldownLoading] = useState<boolean>(false);
  const [drilldownError, setDrilldownError] = useState<SlaApiError | null>(null);
  /** Testo del filtro Cliente; i valori si applicano su blur/Invio, non a ogni tasto. */
  const [customerInput, setCustomerInput] = useState<string>("");
  const [customerValues, setCustomerValues] = useState<string | undefined>(undefined);
  /** Il modello scelto ha una variabile flaggata `isStatisticsKey`? */
  const [modelHasStatisticsKey, setModelHasStatisticsKey] = useState<boolean>(false);

  /** Periodo `[from, to)` dichiarato dalla superficie: e' quello che viene inviato. */
  const period = useMemo(() => slaStatisticsPeriod(interval), [interval]);
  const periodDays = slaStatisticsPeriodDays(period);
  const periodWithinLimit = slaStatisticsPeriodWithinLimit(period);

  /** Risposte fuori ordine: solo l'ultima richiesta applicata scrive lo stato. */
  const requestIdRef = useRef<number>(0);
  const trendRequestIdRef = useRef<number>(0);
  const drilldownRequestIdRef = useRef<number>(0);
  const breakdownRequestIdRef = useRef<number>(0);
  const [refreshToken, setRefreshToken] = useState<number>(0);
  const requestRefresh = useCallback(() => setRefreshToken((current) => current + 1), []);

  /** Rientro sul tab: la metrica non resta "fresca" perche' la pagina era aperta. */
  const { nowMs } = useSlaRuntimeRefresh(() => false, requestRefresh);

  useEffect(() => {
    if (!periodWithinLimit) {
      // Periodo oltre il contratto (399 giorni): la superficie dichiara il
      // limite e **non** invia la richiesta (il Server risponderebbe 422).
      setData(null);
      setError(null);
      setLoading(false);
      setTrend(null);
      setTrendError(null);
      setTrendLoading(false);
      return;
    }
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    (async () => {
      try {
        const response = await fetchSlaCompliance(
          idorganization,
          {
            ...slaStatisticsQuery(level, period, modelId),
            ...(customerValues ? { customervalues: customerValues } : {}),
          },
        );
        if (requestId !== requestIdRef.current) return;
        setData(response);
        setError(null);
      } catch (caught) {
        if (requestId !== requestIdRef.current) return;
        // Nessun numero residuo e nessun verde: la conformita' dichiara l'errore.
        setData(null);
        setError(toSlaApiError(caught));
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    })();
  }, [
    idorganization,
    level,
    period,
    periodWithinLimit,
    modelId,
    customerValues,
    refreshToken,
  ]);

  useEffect(() => {
    if (!periodWithinLimit) return;
    const requestId = trendRequestIdRef.current + 1;
    trendRequestIdRef.current = requestId;
    setTrendLoading(true);
    (async () => {
      try {
        const response = await fetchSlaTrend(
          idorganization,
          slaStatisticsTrendQuery(level, period, granularity, modelId, customerValues),
        );
        if (requestId !== trendRequestIdRef.current) return;
        setTrend(response);
        setTrendError(null);
      } catch (caught) {
        if (requestId !== trendRequestIdRef.current) return;
        setTrend(null);
        setTrendError(toSlaApiError(caught));
      } finally {
        if (requestId === trendRequestIdRef.current) setTrendLoading(false);
      }
    })();
  }, [
    idorganization,
    level,
    period,
    granularity,
    periodWithinLimit,
    modelId,
    customerValues,
    refreshToken,
  ]);

  /** Il modello scelto ha una variabile flaggata `isStatisticsKey`? Una lettura sola. */
  useEffect(() => {
    if (typeof modelId !== "number" || !Number.isFinite(modelId)) {
      setModelHasStatisticsKey(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: model } = await processmodelsApi.get(String(modelId));
        if (cancelled) return;
        setModelHasStatisticsKey(slaStatisticsHasStatisticsKey(model?.userschema));
      } catch {
        // Nessuna prova del flag: il filtro Cliente resta non offerto.
        if (!cancelled) setModelHasStatisticsKey(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modelId]);

  useEffect(() => {
    if (!periodWithinLimit) {
      setBreakdown(null);
      setBreakdownError(null);
      setBreakdownLoading(false);
      return;
    }
    const requestId = breakdownRequestIdRef.current + 1;
    breakdownRequestIdRef.current = requestId;
    setBreakdownLoading(true);
    (async () => {
      try {
        const response = await fetchSlaModelBreakdown(idorganization, {
          ...slaStatisticsQuery(level, period, modelId),
          ...(customerValues ? { customervalues: customerValues } : {}),
        });
        if (requestId !== breakdownRequestIdRef.current) return;
        setBreakdown(response);
        setBreakdownError(null);
      } catch (caught) {
        if (requestId !== breakdownRequestIdRef.current) return;
        setBreakdown(null);
        setBreakdownError(toSlaApiError(caught));
      } finally {
        if (requestId === breakdownRequestIdRef.current) setBreakdownLoading(false);
      }
    })();
  }, [
    idorganization,
    level,
    period,
    periodWithinLimit,
    modelId,
    customerValues,
    refreshToken,
  ]);

  useEffect(() => {
    if (!drilldownTarget || !periodWithinLimit) {
      setDrilldown(null);
      setDrilldownError(null);
      setDrilldownLoading(false);
      return;
    }
    const requestId = drilldownRequestIdRef.current + 1;
    drilldownRequestIdRef.current = requestId;
    setDrilldownLoading(true);
    (async () => {
      try {
        const response = await fetchSlaDrilldown(
          idorganization,
          level,
          slaStatisticsDrilldownQuery(
            { from: drilldownTarget.from, to: drilldownTarget.to },
            drilldownPage,
            drilldownTarget.processModelId ?? modelId,
            customerValues,
          ),
        );
        if (requestId !== drilldownRequestIdRef.current) return;
        setDrilldown(response);
        setDrilldownError(null);
      } catch (caught) {
        if (requestId !== drilldownRequestIdRef.current) return;
        setDrilldown(null);
        setDrilldownError(toSlaApiError(caught));
      } finally {
        if (requestId === drilldownRequestIdRef.current) setDrilldownLoading(false);
      }
    })();
  }, [
    idorganization,
    level,
    drilldownTarget,
    drilldownPage,
    periodWithinLimit,
    modelId,
    customerValues,
    refreshToken,
  ]);

  /** Filtro Cliente: si applica su Invio o all'uscita dal campo, non a ogni tasto. */
  const commitCustomerInput = useCallback(() => {
    setCustomerValues(slaStatisticsCustomerValues(customerInput));
  }, [customerInput]);

  /** Il drill-down si apre dal punto aggregato e si richiude con lo stesso punto. */
  const toggleDrilldown = useCallback(
    (target: DrilldownTarget) => {
      setDrilldownPage(1);
      setDrilldownTarget((current) =>
        current &&
        current.bucket === target.bucket &&
        (current.processModelId ?? null) === (target.processModelId ?? null)
          ? null
          : target,
      );
    },
    [],
  );

  /** Escape richiude il drill-down (sez. 6: tastiera ed Escape funzionanti). */
  useEffect(() => {
    if (!drilldownTarget) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDrilldownTarget(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [drilldownTarget]);

  const asOf = data?.as_of ?? null;
  const stale = isSlaMonitorStale(asOf, nowMs);

  const t = useCallback(
    (key: string): string => getTranslation(translations, key),
    [translations],
  );
  /** Testi di layout dal catalogo, con segnaposto (`useSlaStatisticsText`). */
  const ui = useSlaStatisticsText();

  const unavailable = t(SLA_STATISTICS_TEXT_KEYS.rateUnavailable);

  // ─── Formattazione leggibile (lingua dell'utente) ──────────────────────────

  /** Giorno locale `YYYY-MM-DD` del Server reso come data breve localizzata. */
  const formatDay = useCallback(
    (iso: string, withYear = true): string => {
      const parsed = dayjs(iso);
      if (!parsed.isValid()) return iso;
      try {
        return new Intl.DateTimeFormat(locale, {
          day: "2-digit",
          month: "short",
          ...(withYear ? { year: "numeric" } : {}),
        }).format(parsed.toDate());
      } catch {
        return iso;
      }
    },
    [locale],
  );

  /** Istante ISO (es. `as_of`, `closed_at`) nel fuso del browser dell'utente. */
  const formatInstant = useCallback(
    (iso: string | null | undefined): string => {
      if (!iso) return "—";
      const parsed = dayjs(iso);
      if (!parsed.isValid()) return iso;
      try {
        return new Intl.DateTimeFormat(locale, {
          dateStyle: "short",
          timeStyle: "short",
        }).format(parsed.toDate());
      } catch {
        return parsed.format("DD/MM/YYYY HH:mm");
      }
    },
    [locale],
  );

  const percentText = useCallback(
    (base: SlaComplianceBase | null | undefined): string =>
      slaStatisticsRate(base?.percent, base?.eligible ?? 0, locale, unavailable).text,
    [locale, unavailable],
  );

  /** Ultimo giorno **incluso** del periodo `[from, to)`: quello che l'utente ha scelto. */
  const periodLastDay = dayjs(period.to).subtract(1, "day").format("YYYY-MM-DD");

  /**
   * Campione di una vista: `sample` del payload (il contratto lo manda sempre).
   * Gli attributi restano sempre (contratto DOM); il testo si mostra solo se
   * `visible`, cosi' il "Campione 3 / 400" del trend non torna come rumore.
   */
  const renderSample = (
    testId: string,
    sample: SlaComplianceSample | null | undefined,
    visible: boolean,
  ) => {
    const view = slaStatisticsSample(sample);
    return (
      <Box
        component="span"
        data-testid={testId}
        data-sample-rows={view.rows === null ? "" : String(view.rows)}
        data-sample-limit={view.limit === null ? "" : String(view.limit)}
        data-sample-limited={String(view.limited)}
      >
        {visible && (
          <Typography variant="caption" color="text.secondary">
            {`${t(SLA_STATISTICS_TEXT_KEYS.sampleSize)}: ${view.available ? view.text : "—"}`}
          </Typography>
        )}
      </Box>
    );
  };

  /**
   * Icona di aiuto accanto all'etichetta di una scheda KPI: focalizzabile da
   * tastiera (IconButton) e su tocco senza attesa (`enterTouchDelay={0}`).
   */
  const renderHelp = (testId: string, text: string) => (
    <Tooltip
      title={text}
      arrow
      placement="top"
      enterTouchDelay={0}
      slotProps={{ tooltip: { sx: { maxWidth: 320 } } }}
    >
      <IconButton
        size="small"
        aria-label={text}
        data-testid={testId}
        sx={{ p: 0.25, color: "text.secondary" }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );

  /** Riga etichetta + aiuto, allineati in verticale. */
  const kpiLabelRowSx = { display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 } as const;

  /** Scheda KPI: etichetta, valore grande, riga secondaria. Testo sempre in inchiostro. */
  const kpiCardSx = (accent?: string) => ({
    border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
    borderLeft: (theme: Theme) =>
      `4px solid ${accent ?? theme.palette.divider}`,
    borderRadius: 1,
    p: 2,
    minWidth: 0,
    bgcolor: "background.paper",
  });

  /** Scheda di una base: percentuale grande e "x di N in tempo" sotto. */
  const renderBasis = (basis: SlaStatisticsBasis) => {
    const counts = data?.[basis] ?? null;
    const rate = slaStatisticsRate(counts?.percent, counts?.eligible ?? 0, locale, unavailable);
    const label =
      basis === "original"
        ? SLA_STATISTICS_TEXT_KEYS.basisOriginal
        : SLA_STATISTICS_TEXT_KEYS.basisEffective;
    const testId = `${TEST_ID}-basis-${basis}`;
    return (
      <Box
        key={basis}
        data-testid={testId}
        data-basis={basis}
        data-met={counts ? String(counts.met) : ""}
        data-eligible={counts ? String(counts.eligible) : ""}
        sx={kpiCardSx(
          basis === "effective" ? theme.palette.primary.main : theme.palette.text.disabled,
        )}
      >
        <Box sx={kpiLabelRowSx}>
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            data-testid={`${testId}-label`}
          >
            {t(label)}
          </Typography>
          {basis === "effective"
            ? renderHelp("sla-stat-help-effective", ui(SLA_STATISTICS_UI_TEXT_KEYS.helpEffective))
            : renderHelp("sla-stat-help-original", ui(SLA_STATISTICS_UI_TEXT_KEYS.helpOriginal))}
        </Box>
        <Typography
          variant={basis === "effective" ? "h4" : "h5"}
          component="div"
          sx={{ fontWeight: 600, lineHeight: 1.3, mt: 0.5 }}
          data-testid={`${testId}-rate`}
          data-basis={basis}
          data-available={rate.available ? "true" : "false"}
          // Valore grezzo del payload accanto al testo localizzato: un N/D e'
          // riconoscibile come assenza, non come 0%.
          data-percent={rate.available ? String(counts?.percent ?? "") : ""}
          aria-label={`${t(SLA_STATISTICS_TEXT_KEYS.rate)}: ${rate.text}`}
        >
          {rate.text}
        </Typography>
        {/* Denominatore esplicito: e' `eligible` (SLA_STATISTICS_TEXT_KEYS.denominator), non il totale delle chiusure. */}
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          data-testid={`${testId}-denominator`}
          data-eligible={counts ? String(counts.eligible) : ""}
          title={t(SLA_STATISTICS_TEXT_KEYS.denominator)}
        >
          {counts
            ? ui(SLA_STATISTICS_UI_TEXT_KEYS.metOf, {
                met: counts.met,
                total: counts.eligible,
              })
            : "—"}
        </Typography>
      </Box>
    );
  };

  const coverage = slaStatisticsNullableRate(
    data?.coverage_percent,
    locale,
    unavailable,
  );
  const average = slaStatisticsWorkingDuration(
    data?.average_working_seconds,
    locale,
    unavailable,
  );
  const averageText = average.available
    ? slaStatisticsFormatWorkingSeconds(data?.average_working_seconds, locale) ?? unavailable
    : average.text;

  /** Campione della conformita' (`sample` del payload, non ricalcolato). */
  const sampleView = slaStatisticsSample(data?.sample);

  const customerFilterState: CustomerFilterState =
    typeof modelId !== "number" || !Number.isFinite(modelId)
      ? "no_model"
      : modelHasStatisticsKey
        ? "available"
        : "blocked";

  /**
   * Riepilogo dei filtri inviati, per l'export: le **stesse** chiavi del contratto
   * P2 che la superficie manda (`modelids`, `customervalues`), in forma di query
   * string. Nessuna prosa e nessuna etichetta: il documento dichiara con che filtro
   * e' stato prodotto.
   */
  const exportFilters = useMemo(
    () =>
      slaStatisticsExportFilterSummary({
        ...(typeof modelId === "number" && Number.isFinite(modelId)
          ? { modelids: String(modelId) }
          : {}),
        ...(customerValues ? { customervalues: customerValues } : {}),
      }),
    [modelId, customerValues],
  );

  // ─── Trend: serie del grafico e righe della tabella per periodo ────────────

  /** Un punto per bucket; `null` = N/D (nessun completamento), mai 0%. */
  const chartData = useMemo(
    () =>
      (trend?.data ?? []).map((point) => ({
        bucket: point.bucket,
        effective:
          point.effective.eligible > 0 && point.effective.percent !== null
            ? point.effective.percent
            : null,
        original:
          point.original.eligible > 0 && point.original.percent !== null
            ? point.original.percent
            : null,
        point,
      })),
    [trend],
  );

  const [trendSort, setTrendSort] = useState<"bucket" | "effective">("bucket");
  const [trendSortDirection, setTrendSortDirection] = useState<"asc" | "desc">("asc");
  const [trendPage, setTrendPage] = useState<number>(0);
  const TREND_ROWS_PER_PAGE = 15;

  const sortedTrendPoints = useMemo(() => {
    const points = [...(trend?.data ?? [])];
    const factor = trendSortDirection === "asc" ? 1 : -1;
    points.sort((a, b) => {
      if (trendSort === "effective") {
        // N/D in fondo in entrambe le direzioni: non e' un valore basso.
        const left = a.effective.eligible > 0 ? a.effective.percent : null;
        const right = b.effective.eligible > 0 ? b.effective.percent : null;
        if (left === null && right === null) return a.bucket.localeCompare(b.bucket);
        if (left === null) return 1;
        if (right === null) return -1;
        return (left - right) * factor;
      }
      return a.bucket.localeCompare(b.bucket) * factor;
    });
    return points;
  }, [trend, trendSort, trendSortDirection]);

  useEffect(() => setTrendPage(0), [trend, trendSort, trendSortDirection]);

  const pagedTrendPoints =
    sortedTrendPoints.length > TREND_ROWS_PER_PAGE
      ? sortedTrendPoints.slice(
          trendPage * TREND_ROWS_PER_PAGE,
          (trendPage + 1) * TREND_ROWS_PER_PAGE,
        )
      : sortedTrendPoints;

  const changeTrendSort = (column: "bucket" | "effective") => {
    if (trendSort === column) {
      setTrendSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setTrendSort(column);
      setTrendSortDirection(column === "effective" ? "desc" : "asc");
    }
  };

  // ─── Per processo: ripartizione per modello, ordinabile e paginata ─────────

  const [breakdownSort, setBreakdownSort] = useState<BreakdownSortColumn>("eligible");
  const [breakdownSortDirection, setBreakdownSortDirection] = useState<"asc" | "desc">("desc");
  const [breakdownPage, setBreakdownPage] = useState<number>(0);

  const breakdownName = useCallback(
    (row: SlaModelBreakdownRow): string =>
      row.name?.trim() ||
      (row.process_model_id === null
        ? ui(SLA_STATISTICS_UI_TEXT_KEYS.byModelUnknown)
        : `#${row.process_model_id}`),
    [ui],
  );

  const sortedBreakdownRows = useMemo(() => {
    const rows = [...(breakdown?.data ?? [])];
    const factor = breakdownSortDirection === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      if (breakdownSort === "effective") {
        // N/D in fondo in entrambe le direzioni: non e' un valore basso.
        const left = a.effective.eligible > 0 ? a.effective.percent : null;
        const right = b.effective.eligible > 0 ? b.effective.percent : null;
        if (left === null && right === null) return b.effective.eligible - a.effective.eligible;
        if (left === null) return 1;
        if (right === null) return -1;
        return (left - right) * factor || b.effective.eligible - a.effective.eligible;
      }
      if (breakdownSort === "name") {
        return breakdownName(a).localeCompare(breakdownName(b), locale) * factor;
      }
      return (a.effective.eligible - b.effective.eligible) * factor;
    });
    return rows;
  }, [breakdown, breakdownSort, breakdownSortDirection, breakdownName, locale]);

  useEffect(
    () => setBreakdownPage(0),
    [breakdown, breakdownSort, breakdownSortDirection],
  );

  const pagedBreakdownRows = sortedBreakdownRows.slice(
    breakdownPage * BREAKDOWN_ROWS_PER_PAGE,
    (breakdownPage + 1) * BREAKDOWN_ROWS_PER_PAGE,
  );

  const changeBreakdownSort = (column: BreakdownSortColumn) => {
    if (breakdownSort === column) {
      setBreakdownSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setBreakdownSort(column);
      setBreakdownSortDirection(column === "name" ? "asc" : "desc");
    }
  };

  /** Riassunto testuale del grafico per lettori di schermo (non solo colore/forma). */
  const trendSummary = useMemo(() => {
    if (!trend || trend.data.length === 0) return "";
    const first = trend.data[0];
    const last = trend.data[trend.data.length - 1];
    return `${ui(SLA_STATISTICS_UI_TEXT_KEYS.trendAria)}. ${formatDay(first.bucket)}: ${percentText(
      first.effective,
    )} · ${formatDay(last.bucket)}: ${percentText(last.effective)}`;
  }, [trend, ui, formatDay, percentText]);

  const effectiveColor = theme.palette.primary.main;
  const originalColor = theme.palette.text.secondary;

  const outcomeLabel = (completionKind: number): string => {
    if (completionKind === 1) return ui(SLA_STATISTICS_UI_TEXT_KEYS.tableCompleted);
    const outcome = SLA_STATISTICS_EXCLUDED_OUTCOMES.find(
      (candidate) => candidate.completionKind === completionKind,
    );
    return outcome ? ui(outcome.key) : String(completionKind);
  };

  /** Esito di una base per una riga di dettaglio: in tempo / violato / N/D. */
  const renderMet = (met: boolean | null) =>
    met === null ? (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    ) : (
      <Chip
        size="small"
        variant="outlined"
        color={met ? "success" : "error"}
        icon={met ? <CheckCircleOutlineIcon /> : <HighlightOffIcon />}
        label={ui(
          met ? SLA_STATISTICS_UI_TEXT_KEYS.tableOnTime : SLA_STATISTICS_UI_TEXT_KEYS.tableBreached,
        )}
      />
    );

  const hasCustomerValues = (drilldown?.data ?? []).some((row) => row.customer_value);

  return (
    <Box
      id={TEST_ID}
      data-testid={`${TEST_ID}-surface`}
      data-level={level}
      data-granularity={granularity}
      data-from={period.from}
      data-to={period.to}
      data-days={String(periodDays)}
      data-period-limited={periodWithinLimit ? "false" : "true"}
      // Target del dato: l'organizzazione di cui la metrica parla (niente obiettivo
      // inventato: il contratto P2 non ha un campo di target).
      data-organization={String(idorganization)}
      // Dimensione resa: nessun utente, ruolo o livello di escalation nel payload.
      data-dimension={SLA_STATISTICS_DIMENSION}
      // Identita' delle righe di dettaglio: `sla_result.idinstance`, una per istanza.
      data-row-identity={SLA_STATISTICS_ROW_IDENTITY}
      data-customer-filter={customerFilterState === "available" ? "true" : "false"}
      data-customer-values={customerValues ?? ""}
      data-stale={String(stale)}
      data-stale-source={SLA_STATISTICS_STALE_SOURCE}
      data-as-of={asOf ?? ""}
      data-has-data={data ? "true" : "false"}
    >
      <Stack spacing={2.5} sx={{ width: "100%", minWidth: 0 }}>
        {/* ── Barra: titolo + livello + Cliente a sinistra, export a destra ── */}
        <Stack spacing={0.5}>
          <Stack
            direction={isCompact ? "column" : "row"}
            spacing={1.5}
            alignItems={isCompact ? "stretch" : "center"}
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ minWidth: 0 }}
            >
              <Typography variant="h6" data-testid={`${TEST_ID}-title`}>
                {t(SLA_STATISTICS_TEXT_KEYS.title)}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                data-testid={`${TEST_ID}-filters`}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  id={`${TEST_ID}-level-label`}
                >
                  {t(SLA_STATISTICS_TEXT_KEYS.level)}
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={level}
                  onChange={(_event, next) => {
                    if (next !== null) {
                      setLevel(next as SlaComplianceLevel);
                      setDrilldownTarget(null);
                    }
                  }}
                  aria-labelledby={`${TEST_ID}-level-label`}
                  data-testid={`${TEST_ID}-level`}
                  data-level={level}
                >
                  {SLA_STATISTICS_LEVELS.map((candidate) => (
                    <ToggleButton
                      key={candidate}
                      value={candidate}
                      data-testid={`${TEST_ID}-level-${candidate}`}
                      sx={{ textTransform: "none", py: 0.25 }}
                    >
                      {t(slaStatisticsLevelKey(candidate))}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
              <Box data-testid={`${TEST_ID}-customer`} sx={{ minWidth: 0 }}>
                {customerFilterState === "available" ? (
                  <TextField
                    size="small"
                    label={t(SLA_STATISTICS_TEXT_KEYS.customerFilter)}
                    value={customerInput}
                    onChange={({ target }) => setCustomerInput(target.value)}
                    onBlur={commitCustomerInput}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") commitCustomerInput();
                    }}
                    inputProps={{ "data-testid": `${TEST_ID}-customer-input` }}
                    // Il contratto vuole i valori separati da virgola (`parse_list_param`).
                    placeholder="ACME, Beta"
                    sx={{ width: 200 }}
                  />
                ) : customerFilterState === "blocked" ? (
                  // Nessuna variabile-chiave flaggata nel modello scelto: la **guida**
                  // del catalogo al posto del filtro, mai un comando inerte (spec UI
                  // sez. 8.6), come icona con tooltip per non occupare una riga.
                  <Tooltip title={t(SLA_STATISTICS_TEXT_KEYS.customerDisabled)}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      data-testid={`${TEST_ID}-customer-disabled`}
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      {t(SLA_STATISTICS_TEXT_KEYS.customerDisabled)}
                    </Typography>
                  </Tooltip>
                ) : (
                  // Nessun modello scelto: nulla da configurare, nessun testo fuorviante.
                  <Box component="span" data-testid={`${TEST_ID}-customer-no-model`} />
                )}
              </Box>
            </Stack>
            {/* F4c: export (impostazioni e preferenze SLA: vista organizzazione). */}
            <Box data-testid={`${TEST_ID}-f4c`} sx={{ flexShrink: 0 }}>
              <SlaStatisticsExportMenu
                locale={locale}
                period={period}
                level={level}
                filters={exportFilters}
                compliance={data}
                trend={trend}
                drilldown={drilldown}
              />
            </Box>
          </Stack>

          {/* Periodo, timezone del payload e aggiornamento: una sola riga di didascalia. */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="baseline"
            flexWrap="wrap"
            useFlexGap
            data-testid={`${TEST_ID}-period`}
            data-from={period.from}
            data-to={period.to}
            data-timezone={data?.timezone ?? ""}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid={`${TEST_ID}-period-value`}
              title={`${t(SLA_STATISTICS_TEXT_KEYS.period)} [${period.from}, ${period.to})`}
            >
              {`${formatDay(period.from)} – ${formatDay(periodLastDay)}`}
            </Typography>
            <Typography variant="caption" color="text.disabled" aria-hidden>
              ·
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid={`${TEST_ID}-period-timezone`}
            >
              {`${t(SLA_STATISTICS_TEXT_KEYS.periodTimezone)} ${data?.timezone ?? "—"}`}
            </Typography>
            <Typography variant="caption" color="text.disabled" aria-hidden>
              ·
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid={`${TEST_ID}-as-of`}
            >
              {`${t(SLA_MONITOR_TEXT_KEYS.asOf)} ${formatInstant(asOf)}`}
            </Typography>
          </Stack>
        </Stack>

        {isSlaFixtureMode() && (
          <Alert severity="info">{t("sla.editor.fixture_notice")}</Alert>
        )}

        {stale && (
          <Alert severity="warning" data-testid={`${TEST_ID}-stale`}>
            {t(SLA_MONITOR_TEXT_KEYS.stale)}
          </Alert>
        )}

        {error && (
          <Alert
            severity="error"
            data-testid={`${TEST_ID}-error`}
            data-error-code={error.code ?? ""}
            data-error-status={error.status === null ? "" : String(error.status)}
          >
            {t(
              error.status === 403
                ? SLA_MONITOR_TEXT_KEYS.forbidden
                : SLA_MONITOR_TEXT_KEYS.loadError,
            )}
          </Alert>
        )}

        {!periodWithinLimit && (
          <Alert severity="warning" data-testid={`${TEST_ID}-period-limit`}>
            {t(SLA_STATISTICS_TEXT_KEYS.periodLimit)}
          </Alert>
        )}

        {loading && !data && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={20} data-testid={`${TEST_ID}-loading`} />
            <Typography variant="caption" color="text.secondary">
              {t("generic.loading")}
            </Typography>
          </Stack>
        )}

        {!loading && !error && !data && periodWithinLimit && (
          <Alert severity="info" data-testid={`${TEST_ID}-no-data`}>
            {t(SLA_STATISTICS_TEXT_KEYS.noData)}
          </Alert>
        )}

        {/* ── Schede KPI ─────────────────────────────────────────────────────── */}
        {data && (
          <Stack spacing={1} data-testid={`${TEST_ID}-compliance`}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="subtitle1" data-testid={`${TEST_ID}-compliance-title`}>
                {t(slaStatisticsMessageKey(data, "title"))}
              </Typography>
              {/* Effettiva vs originale: spiegazione una volta, su richiesta. */}
              <Tooltip title={t(slaStatisticsMessageKey(data, "note"))}>
                <IconButton
                  size="small"
                  aria-label={t(slaStatisticsMessageKey(data, "note"))}
                  data-testid={`${TEST_ID}-note`}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", lg: "2fr 3fr" },
              }}
            >
              {/* Due basi, chiaramente etichettate e affiancate. */}
              <Box
                data-testid={`${TEST_ID}-bases`}
                data-bases={SLA_STATISTICS_BASES.join(",")}
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  minWidth: 0,
                }}
              >
                {[...SLA_STATISTICS_BASES].reverse().map((basis) => renderBasis(basis))}
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
                  minWidth: 0,
                }}
              >
                <Box sx={kpiCardSx()}>
                  <Box sx={kpiLabelRowSx}>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {t(SLA_STATISTICS_TEXT_KEYS.sampleSize)}
                    </Typography>
                    {renderHelp(
                      "sla-stat-help-sample-size",
                      ui(SLA_STATISTICS_UI_TEXT_KEYS.helpSampleSize),
                    )}
                  </Box>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 600, mt: 0.5 }}>
                    <Box
                      component="span"
                      data-testid={`${TEST_ID}-sample`}
                      data-sample-rows={sampleView.rows === null ? "" : String(sampleView.rows)}
                      data-sample-limit={sampleView.limit === null ? "" : String(sampleView.limit)}
                      data-sample-limited={String(sampleView.limited)}
                    >
                      {sampleView.available ? sampleView.text : "—"}
                    </Box>
                  </Typography>
                </Box>
                <Box
                  sx={kpiCardSx()}
                  data-testid={`${TEST_ID}-coverage`}
                  data-available={coverage.available ? "true" : "false"}
                  data-coverage-percent={
                    coverage.available ? String(data.coverage_percent ?? "") : ""
                  }
                >
                  <Typography variant="caption" color="text.secondary" component="div">
                    {t(slaStatisticsMessageKey(data, "coverage"))}
                  </Typography>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {coverage.text}
                  </Typography>
                </Box>
                <Box
                  sx={kpiCardSx()}
                  data-testid={`${TEST_ID}-average-working-time`}
                  data-available={average.available ? "true" : "false"}
                  data-seconds={
                    average.available ? String(data.average_working_seconds ?? "") : ""
                  }
                >
                  <Typography variant="caption" color="text.secondary" component="div">
                    {t(slaStatisticsMessageKey(data, "average_working_time"))}
                  </Typography>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 600, mt: 0.5 }}>
                    {averageText}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Esiti fuori dal denominatore: una sola riga, mai sommati a `eligible`. */}
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="baseline"
              flexWrap="wrap"
              useFlexGap
              data-testid={`${TEST_ID}-excluded`}
              data-cancelled={data.excluded ? String(data.excluded.cancelled) : ""}
              data-interrupted={data.excluded ? String(data.excluded.interrupted) : ""}
              data-authority-terminated={
                data.excluded ? String(data.excluded.authority_terminated) : ""
              }
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {`${ui(SLA_STATISTICS_UI_TEXT_KEYS.excludedTitle)}:`}
              </Typography>
              {SLA_STATISTICS_EXCLUDED_OUTCOMES.map((field) => (
                <Typography
                  key={field.field}
                  variant="caption"
                  color="text.secondary"
                  data-testid={`${TEST_ID}-excluded-${field.field}`}
                  data-excluded-field={field.field}
                  data-completion-kind={String(field.completionKind)}
                  data-count={String(data.excluded?.[field.field] ?? 0)}
                >
                  {`${ui(field.key)} ${data.excluded?.[field.field] ?? 0}`}
                </Typography>
              ))}
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid={`${TEST_ID}-without-sla`}
                data-without-sla={String(data.without_sla)}
              >
                {`${t(SLA_STATISTICS_TEXT_KEYS.excludedNoSla)} ${data.without_sla}`}
              </Typography>
            </Stack>
          </Stack>
        )}

        {/* ── F4b: trend (grafico + tabella per periodo) ──────────────────────── */}
        {periodWithinLimit && (
          <Box
            data-testid={`${TEST_ID}-trend`}
            data-trend-level={level}
            sx={{
              border: (th) => `1px solid ${th.palette.divider}`,
              borderRadius: 1,
              p: 2,
              minWidth: 0,
            }}
          >
            <Stack spacing={1.5}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                useFlexGap
              >
                <Typography
                  variant="subtitle1"
                  id={`${TEST_ID}-trend-title`}
                  data-testid={`${TEST_ID}-trend-title`}
                >
                  {t(SLA_STATISTICS_TEXT_KEYS.trendTitle)}
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={granularity}
                  onChange={(_event, next) => {
                    if (next !== null) {
                      setGranularity(next as SlaStatisticsGranularity);
                      setDrilldownTarget(null);
                    }
                  }}
                  aria-labelledby={`${TEST_ID}-trend-title`}
                  data-testid={`${TEST_ID}-granularity`}
                  data-granularity={granularity}
                >
                  {SLA_STATISTICS_GRANULARITIES.map((candidate) => (
                    <ToggleButton
                      key={candidate}
                      value={candidate}
                      data-testid={`${TEST_ID}-granularity-${candidate}`}
                      sx={{ textTransform: "none", py: 0.25 }}
                    >
                      {t(slaStatisticsGranularityKey(candidate))}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>

              {trendError && (
                <Alert
                  severity="error"
                  data-testid={`${TEST_ID}-trend-error`}
                  data-error-status={trendError.status === null ? "" : String(trendError.status)}
                >
                  {t(
                    trendError.status === 403
                      ? SLA_MONITOR_TEXT_KEYS.forbidden
                      : SLA_MONITOR_TEXT_KEYS.loadError,
                  )}
                </Alert>
              )}

              {trendLoading && !trend && (
                <CircularProgress size={20} data-testid={`${TEST_ID}-trend-loading`} />
              )}

              {trend && (
                <Box
                  data-testid={`${TEST_ID}-trend-meta`}
                  data-granularity={trend.granularity}
                  data-trend-from={trend.period?.from ?? ""}
                  data-trend-to={trend.period?.to ?? ""}
                  data-trend-timezone={trend.timezone}
                  data-points={String(trend.data.length)}
                >
                  {/* Il campione del trend (bucket / tetto 400) si mostra solo se troncato. */}
                  {renderSample(`${TEST_ID}-trend-sample`, trend.sample, trend.sample?.limited === true)}
                </Box>
              )}

              {trend && trend.data.length === 0 && (
                <Alert severity="info" variant="outlined" data-testid={`${TEST_ID}-trend-empty`}>
                  {t(SLA_STATISTICS_TEXT_KEYS.noData)}
                </Alert>
              )}

              {trend && trend.data.length > 0 && (
                <>
                  {/* Grafico: una sola scala 0-100%, effettiva (primario) vs originale (neutro, tratteggiata). */}
                  <Box
                    role="img"
                    aria-label={trendSummary}
                    data-testid={`${TEST_ID}-trend-chart`}
                    sx={{ width: "100%", height: isCompact ? 220 : 280, minWidth: 0 }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={chartData}
                        margin={{ top: 8, right: 16, bottom: 0, left: -8 }}
                        onClick={(state) => {
                          const bucket = (
                            state?.activePayload?.[0]?.payload as { bucket?: string } | undefined
                          )?.bucket;
                          if (bucket) {
                            toggleDrilldown({
                              ...slaStatisticsBucketRange(bucket, trend.granularity),
                              bucket,
                            });
                          }
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <CartesianGrid stroke={theme.palette.divider} vertical={false} />
                        <XAxis
                          dataKey="bucket"
                          tickFormatter={(value: string) => formatDay(value, false)}
                          tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                          stroke={theme.palette.divider}
                          minTickGap={16}
                        />
                        <YAxis
                          domain={[0, 100]}
                          ticks={[0, 25, 50, 75, 100]}
                          tickFormatter={(value: number) => `${value}%`}
                          tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                          stroke={theme.palette.divider}
                          width={48}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            background: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 4,
                            color: theme.palette.text.primary,
                            fontSize: 12,
                          }}
                          labelFormatter={(value) => formatDay(String(value))}
                          formatter={(_value, name, item) => {
                            const point = (item?.payload as { point?: SlaTrendPoint } | undefined)
                              ?.point;
                            const base =
                              name === "effective" ? point?.effective : point?.original;
                            const text = percentText(base);
                            const counts = base
                              ? ` (${ui(SLA_STATISTICS_UI_TEXT_KEYS.metOf, {
                                  met: base.met,
                                  total: base.eligible,
                                })})`
                              : "";
                            return [
                              `${text}${counts}`,
                              t(
                                name === "effective"
                                  ? SLA_STATISTICS_TEXT_KEYS.basisEffective
                                  : SLA_STATISTICS_TEXT_KEYS.basisOriginal,
                              ),
                            ];
                          }}
                        />
                        <Legend
                          formatter={(value: string) => (
                            <span style={{ color: theme.palette.text.primary, fontSize: 12 }}>
                              {t(
                                value === "effective"
                                  ? SLA_STATISTICS_TEXT_KEYS.basisEffective
                                  : SLA_STATISTICS_TEXT_KEYS.basisOriginal,
                              )}
                            </span>
                          )}
                        />
                        <Line
                          type="monotone"
                          dataKey="effective"
                          name="effective"
                          stroke={effectiveColor}
                          strokeWidth={2}
                          dot={{ r: 4, fill: effectiveColor, strokeWidth: 0 }}
                          activeDot={{ r: 6 }}
                          connectNulls={false}
                          isAnimationActive={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="original"
                          name="original"
                          stroke={originalColor}
                          strokeWidth={2}
                          strokeDasharray="5 4"
                          dot={{ r: 4, fill: originalColor, strokeWidth: 0 }}
                          activeDot={{ r: 6 }}
                          connectNulls={false}
                          isAnimationActive={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Tabella per periodo: e' anche la vista testuale del grafico. Ogni
                      riga e' un punto (identita' = `bucket`) e apre il dettaglio. */}
                  <Typography variant="caption" color="text.secondary">
                    {ui(SLA_STATISTICS_UI_TEXT_KEYS.tableHint)}
                  </Typography>
                  <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
                    <Table size="small" data-testid={`${TEST_ID}-trend-points`}>
                      <TableHead>
                        <TableRow>
                          <TableCell sortDirection={trendSort === "bucket" ? trendSortDirection : false}>
                            <TableSortLabel
                              sx={SORT_LABEL_SX}
                              active={trendSort === "bucket"}
                              direction={trendSort === "bucket" ? trendSortDirection : "asc"}
                              onClick={() => changeTrendSort("bucket")}
                            >
                              {ui(slaStatisticsGranularityKey(trend.granularity))}
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">
                            {ui(SLA_STATISTICS_UI_TEXT_KEYS.tableCompleted)}
                          </TableCell>
                          <TableCell align="right">
                            {ui(SLA_STATISTICS_UI_TEXT_KEYS.tableOnTime)}
                          </TableCell>
                          <TableCell align="right">
                            {ui(SLA_STATISTICS_UI_TEXT_KEYS.tableBreached)}
                          </TableCell>
                          <TableCell
                            sx={{ minWidth: 160 }}
                            sortDirection={trendSort === "effective" ? trendSortDirection : false}
                          >
                            <TableSortLabel
                              sx={SORT_LABEL_SX}
                              active={trendSort === "effective"}
                              direction={trendSort === "effective" ? trendSortDirection : "desc"}
                              onClick={() => changeTrendSort("effective")}
                            >
                              {ui(SLA_STATISTICS_TEXT_KEYS.basisEffective)}
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">
                            {ui(SLA_STATISTICS_TEXT_KEYS.basisOriginal)}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedTrendPoints.map((point) => {
                          const bucketRange = slaStatisticsBucketRange(
                            point.bucket,
                            trend.granularity,
                          );
                          const selected =
                            drilldownTarget?.bucket === point.bucket &&
                            !drilldownTarget?.processModelId;
                          const originalRate = slaStatisticsRate(
                            point.original.percent,
                            point.original.eligible,
                            locale,
                            unavailable,
                          );
                          const effectiveRate = slaStatisticsRate(
                            point.effective.percent,
                            point.effective.eligible,
                            locale,
                            unavailable,
                          );
                          const open = () =>
                            toggleDrilldown({ ...bucketRange, bucket: point.bucket });
                          return (
                            <TableRow
                              key={point.bucket}
                              hover
                              selected={selected}
                              onClick={open}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  open();
                                }
                              }}
                              tabIndex={0}
                              role="button"
                              aria-pressed={selected}
                              data-testid={`${TEST_ID}-trend-point`}
                              data-bucket={point.bucket}
                              data-selected={String(selected)}
                              data-original-met={String(point.original.met)}
                              data-original-eligible={String(point.original.eligible)}
                              data-original-percent={
                                point.original.percent === null
                                  ? ""
                                  : String(point.original.percent)
                              }
                              data-effective-met={String(point.effective.met)}
                              data-effective-eligible={String(point.effective.eligible)}
                              data-effective-percent={
                                point.effective.percent === null
                                  ? ""
                                  : String(point.effective.percent)
                              }
                              data-without-sla={String(point.without_sla)}
                              data-cancelled={String(point.excluded?.cancelled ?? 0)}
                              data-interrupted={String(point.excluded?.interrupted ?? 0)}
                              data-authority-terminated={String(
                                point.excluded?.authority_terminated ?? 0,
                              )}
                              sx={{ cursor: "pointer" }}
                            >
                              <TableCell data-testid={`${TEST_ID}-trend-bucket`} sx={{ whiteSpace: "nowrap" }}>
                                {formatDay(point.bucket)}
                              </TableCell>
                              <TableCell align="right">{point.effective.eligible}</TableCell>
                              <TableCell align="right">{point.effective.met}</TableCell>
                              <TableCell align="right">
                                {Math.max(point.effective.eligible - point.effective.met, 0)}
                              </TableCell>
                              <TableCell
                                data-testid={`${TEST_ID}-trend-point-effective`}
                                data-basis="effective"
                                data-available={effectiveRate.available ? "true" : "false"}
                              >
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <LinearProgress
                                    variant="determinate"
                                    value={
                                      effectiveRate.available
                                        ? Math.min(Math.max(point.effective.percent ?? 0, 0), 100)
                                        : 0
                                    }
                                    aria-hidden
                                    sx={{ flex: 1, minWidth: 48, height: 6, borderRadius: 3 }}
                                  />
                                  <Typography variant="body2" sx={{ minWidth: 56, textAlign: "right" }}>
                                    {effectiveRate.text}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell
                                align="right"
                                data-testid={`${TEST_ID}-trend-point-original`}
                                data-basis="original"
                                data-available={originalRate.available ? "true" : "false"}
                                sx={{ color: "text.secondary" }}
                              >
                                {originalRate.text}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {sortedTrendPoints.length > TREND_ROWS_PER_PAGE && (
                    <TablePagination
                      component="div"
                      count={sortedTrendPoints.length}
                      page={trendPage}
                      onPageChange={(_event, next) => setTrendPage(next)}
                      rowsPerPage={TREND_ROWS_PER_PAGE}
                      rowsPerPageOptions={[TREND_ROWS_PER_PAGE]}
                    />
                  )}
                </>
              )}
            </Stack>
          </Box>
        )}

        {/* ── Per processo: conformita' per modello (stessi filtri delle schede) ── */}
        {periodWithinLimit && (
          <Box
            data-testid={`${TEST_ID}-by-model`}
            data-level={level}
            data-rows={breakdown ? String(breakdown.data.length) : ""}
            sx={{
              border: (th) => `1px solid ${th.palette.divider}`,
              borderRadius: 1,
              p: 2,
              minWidth: 0,
            }}
          >
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" data-testid={`${TEST_ID}-by-model-title`}>
                {ui(SLA_STATISTICS_UI_TEXT_KEYS.byModelTitle)}
              </Typography>

              {breakdownError && (
                <Alert
                  severity="error"
                  data-testid={`${TEST_ID}-by-model-error`}
                  data-error-status={
                    breakdownError.status === null ? "" : String(breakdownError.status)
                  }
                >
                  {t(
                    breakdownError.status === 403
                      ? SLA_MONITOR_TEXT_KEYS.forbidden
                      : SLA_MONITOR_TEXT_KEYS.loadError,
                  )}
                </Alert>
              )}

              {breakdownLoading && !breakdown && (
                <CircularProgress size={20} data-testid={`${TEST_ID}-by-model-loading`} />
              )}

              {breakdown && breakdown.data.length === 0 && (
                <Alert severity="info" variant="outlined" data-testid={`${TEST_ID}-by-model-empty`}>
                  {t(SLA_STATISTICS_TEXT_KEYS.noData)}
                </Alert>
              )}

              {breakdown && breakdown.data.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    {ui(SLA_STATISTICS_UI_TEXT_KEYS.byModelHint)}
                  </Typography>
                  <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
                    <Table size="small" data-testid={`${TEST_ID}-by-model-table`}>
                      <TableHead>
                        <TableRow>
                          <TableCell sortDirection={breakdownSort === "name" ? breakdownSortDirection : false}>
                            <TableSortLabel
                              sx={SORT_LABEL_SX}
                              active={breakdownSort === "name"}
                              direction={breakdownSort === "name" ? breakdownSortDirection : "asc"}
                              onClick={() => changeBreakdownSort("name")}
                            >
                              {ui(SLA_STATISTICS_UI_TEXT_KEYS.byModelProcess)}
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="right"
                            sortDirection={breakdownSort === "eligible" ? breakdownSortDirection : false}
                          >
                            <TableSortLabel
                              sx={SORT_LABEL_SX}
                              active={breakdownSort === "eligible"}
                              direction={breakdownSort === "eligible" ? breakdownSortDirection : "desc"}
                              onClick={() => changeBreakdownSort("eligible")}
                            >
                              {ui(SLA_STATISTICS_UI_TEXT_KEYS.tableCompleted)}
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">
                            {ui(SLA_STATISTICS_UI_TEXT_KEYS.tableOnTime)}
                          </TableCell>
                          <TableCell align="right">
                            {ui(SLA_STATISTICS_UI_TEXT_KEYS.tableBreached)}
                          </TableCell>
                          <TableCell
                            sx={{ minWidth: 180 }}
                            sortDirection={breakdownSort === "effective" ? breakdownSortDirection : false}
                          >
                            <TableSortLabel
                              sx={SORT_LABEL_SX}
                              active={breakdownSort === "effective"}
                              direction={breakdownSort === "effective" ? breakdownSortDirection : "desc"}
                              onClick={() => changeBreakdownSort("effective")}
                            >
                              {ui(SLA_STATISTICS_UI_TEXT_KEYS.byModelEffective)}
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">
                            {ui(SLA_STATISTICS_UI_TEXT_KEYS.byModelOriginal)}
                          </TableCell>
                          <TableCell align="right">
                            {ui(SLA_STATISTICS_UI_TEXT_KEYS.byModelAverage)}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedBreakdownRows.map((row) => {
                          const name = breakdownName(row);
                          const clickable = row.process_model_id !== null;
                          const selected =
                            clickable &&
                            drilldownTarget?.processModelId === row.process_model_id;
                          const effectiveRate = slaStatisticsRate(
                            row.effective.percent,
                            row.effective.eligible,
                            locale,
                            unavailable,
                          );
                          const originalRate = slaStatisticsRate(
                            row.original.percent,
                            row.original.eligible,
                            locale,
                            unavailable,
                          );
                          // Il dettaglio si restringe al modello sul periodo intero.
                          const open = () => {
                            if (!clickable) return;
                            toggleDrilldown({
                              from: period.from,
                              to: period.to,
                              bucket: null,
                              processModelId: row.process_model_id,
                              processModelName: name,
                            });
                          };
                          return (
                            <TableRow
                              key={row.process_model_id ?? "unknown"}
                              hover={clickable}
                              selected={selected}
                              onClick={open}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  open();
                                }
                              }}
                              tabIndex={clickable ? 0 : -1}
                              role={clickable ? "button" : undefined}
                              aria-pressed={clickable ? selected : undefined}
                              data-testid={`${TEST_ID}-by-model-row`}
                              data-process-model-id={
                                row.process_model_id === null ? "" : String(row.process_model_id)
                              }
                              data-effective-met={String(row.effective.met)}
                              data-effective-eligible={String(row.effective.eligible)}
                              data-effective-percent={
                                row.effective.percent === null ? "" : String(row.effective.percent)
                              }
                              data-original-percent={
                                row.original.percent === null ? "" : String(row.original.percent)
                              }
                              data-selected={String(selected)}
                              sx={{ cursor: clickable ? "pointer" : "default" }}
                            >
                              <TableCell
                                sx={{
                                  maxWidth: 280,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={name}
                              >
                                {name}
                              </TableCell>
                              <TableCell align="right">{row.effective.eligible}</TableCell>
                              <TableCell align="right">{row.effective.met}</TableCell>
                              <TableCell align="right">{Math.max(row.missed, 0)}</TableCell>
                              <TableCell
                                data-basis="effective"
                                data-available={effectiveRate.available ? "true" : "false"}
                              >
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <LinearProgress
                                    variant="determinate"
                                    value={
                                      effectiveRate.available
                                        ? Math.min(Math.max(row.effective.percent ?? 0, 0), 100)
                                        : 0
                                    }
                                    aria-hidden
                                    sx={{ flex: 1, minWidth: 48, height: 6, borderRadius: 3 }}
                                  />
                                  <Typography variant="body2" sx={{ minWidth: 56, textAlign: "right" }}>
                                    {effectiveRate.text}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell
                                align="right"
                                data-basis="original"
                                data-available={originalRate.available ? "true" : "false"}
                                sx={{ color: "text.secondary" }}
                              >
                                {originalRate.text}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                {slaStatisticsFormatWorkingSeconds(
                                  row.average_working_seconds,
                                  locale,
                                ) ?? unavailable}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {sortedBreakdownRows.length > BREAKDOWN_ROWS_PER_PAGE && (
                    <TablePagination
                      component="div"
                      count={sortedBreakdownRows.length}
                      page={breakdownPage}
                      onPageChange={(_event, next) => setBreakdownPage(next)}
                      rowsPerPage={BREAKDOWN_ROWS_PER_PAGE}
                      rowsPerPageOptions={[BREAKDOWN_ROWS_PER_PAGE]}
                    />
                  )}
                </>
              )}
            </Stack>
          </Box>
        )}

        {/* ── F4b: drill-down (tabella delle istanze del periodo scelto) ──────── */}
        {drilldownTarget && (
          <Box
            data-testid={`${TEST_ID}-drilldown`}
            data-drilldown-level={level}
            data-drilldown-bucket={drilldownTarget.bucket ?? ""}
            data-drilldown-from={drilldownTarget.from}
            data-drilldown-to={drilldownTarget.to}
            data-drilldown-page={drilldown ? String(drilldown.pagination.page) : ""}
            data-drilldown-page-size={
              drilldown ? String(drilldown.pagination.page_size) : ""
            }
            data-total-items={drilldown ? String(drilldown.pagination.total_items) : ""}
            data-total-pages={drilldown ? String(drilldown.pagination.total_pages) : ""}
            data-rows={drilldown ? String(drilldown.data.length) : ""}
            sx={{
              border: (th) => `1px solid ${th.palette.divider}`,
              borderRadius: 1,
              p: 2,
              minWidth: 0,
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" data-testid={`${TEST_ID}-drilldown-title`}>
                    {`${t(slaStatisticsMessageKey(drilldown, "drilldown_title"))} · ${t(
                      slaStatisticsDrilldownTitleKey(level),
                    )}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {drilldownTarget.bucket
                      ? formatDay(drilldownTarget.bucket)
                      : `${formatDay(drilldownTarget.from)} – ${formatDay(
                          dayjs(drilldownTarget.to).subtract(1, "day").format("YYYY-MM-DD"),
                        )}`}
                    {drilldownTarget.processModelName
                      ? ` · ${drilldownTarget.processModelName}`
                      : ""}
                  </Typography>
                  {/* La nota del payload: i passaggi ripetuti non si contano due volte. */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    data-testid={`${TEST_ID}-drilldown-note`}
                  >
                    {t(slaStatisticsMessageKey(drilldown, "drilldown_no_double_count"))}
                  </Typography>
                </Stack>
                <IconButton
                  size="small"
                  onClick={() => setDrilldownTarget(null)}
                  aria-label={ui(SLA_STATISTICS_UI_TEXT_KEYS.close)}
                  data-testid={`${TEST_ID}-drilldown-close`}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>

              {drilldownError && (
                <Alert
                  severity="error"
                  data-testid={`${TEST_ID}-drilldown-error`}
                  data-error-status={
                    drilldownError.status === null ? "" : String(drilldownError.status)
                  }
                >
                  {t(
                    drilldownError.status === 403
                      ? SLA_MONITOR_TEXT_KEYS.forbidden
                      : SLA_MONITOR_TEXT_KEYS.loadError,
                  )}
                </Alert>
              )}

              {drilldownLoading && !drilldown && (
                <CircularProgress size={20} data-testid={`${TEST_ID}-drilldown-loading`} />
              )}

              {renderSample(`${TEST_ID}-drilldown-sample`, drilldown?.sample ?? null, false)}

              {drilldown && drilldown.data.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t(SLA_STATISTICS_TEXT_KEYS.noData)}
                </Typography>
              )}

              {drilldown && drilldown.data.length > 0 && (
                <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
                  <Table size="small" data-testid={`${TEST_ID}-drilldown-rows`}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{ui(SLA_STATISTICS_UI_TEXT_KEYS.drilldownInstance)}</TableCell>
                        <TableCell>{ui(SLA_STATISTICS_UI_TEXT_KEYS.drilldownElement)}</TableCell>
                        <TableCell>{ui(SLA_STATISTICS_UI_TEXT_KEYS.drilldownClosedAt)}</TableCell>
                        <TableCell>{t(SLA_STATISTICS_TEXT_KEYS.stateLabel)}</TableCell>
                        <TableCell>{t(SLA_STATISTICS_TEXT_KEYS.basisEffective)}</TableCell>
                        <TableCell>{t(SLA_STATISTICS_TEXT_KEYS.basisOriginal)}</TableCell>
                        <TableCell align="right">
                          {ui(SLA_STATISTICS_UI_TEXT_KEYS.drilldownWorkingTime)}
                        </TableCell>
                        {hasCustomerValues && (
                          <TableCell>{t(SLA_STATISTICS_TEXT_KEYS.customerFilter)}</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {drilldown.data.map((row) => (
                        <TableRow
                          key={row.sla_instance_id}
                          data-testid={`${TEST_ID}-drilldown-row`}
                          data-instance-id={String(row.sla_instance_id)}
                          data-process-id={String(row.process_id)}
                          data-target-type={String(row.target_type)}
                          data-target-designer-id={row.target_designer_id ?? ""}
                          data-target-node-id={
                            row.target_node_id === null ? "" : String(row.target_node_id)
                          }
                          data-closed-at={row.closed_at}
                          data-completion-kind={String(row.completion_kind)}
                          data-eligible={String(row.eligible)}
                          data-original-met={
                            row.original_met === null ? "" : String(row.original_met)
                          }
                          data-effective-met={
                            row.effective_met === null ? "" : String(row.effective_met)
                          }
                          data-consumed-working-seconds={String(row.consumed_working_seconds)}
                          data-customer-value={row.customer_value ?? ""}
                        >
                          {/* Identita' storica della riga: `sla_instance_id` del contratto. */}
                          <TableCell>{`#${row.process_id}`}</TableCell>
                          <TableCell sx={{ maxWidth: 220, overflowWrap: "anywhere" }}>
                            {row.target_type === 2
                              ? row.target_name || row.target_designer_id || row.target_node_id || "—"
                              : t(slaStatisticsLevelKey("process"))}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {formatInstant(row.closed_at)}
                          </TableCell>
                          <TableCell>{outcomeLabel(row.completion_kind)}</TableCell>
                          <TableCell>{renderMet(row.effective_met)}</TableCell>
                          <TableCell>{renderMet(row.original_met)}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            {slaStatisticsFormatWorkingSeconds(
                              row.consumed_working_seconds,
                              locale,
                            ) ?? "—"}
                          </TableCell>
                          {hasCustomerValues && (
                            <TableCell>{row.customer_value ?? "—"}</TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Paginazione dichiarata + "carica altri" quando esistono altre pagine. */}
              {drilldown && (
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" color="text.secondary">
                    {`${drilldown.pagination.page}/${drilldown.pagination.total_pages} · ${drilldown.pagination.total_items}`}
                  </Typography>
                  {drilldown.pagination.page < drilldown.pagination.total_pages && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setDrilldownPage(drilldown.pagination.page + 1)}
                      data-testid={`${TEST_ID}-drilldown-more`}
                    >
                      {t(slaStatisticsMessageKey(drilldown, "load_more"))}
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          </Box>
        )}

        {/* Nessuna metrica per persona: dichiarato una volta, in fondo. */}
        {data && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid={`${TEST_ID}-privacy`}
          >
            {t(slaStatisticsMessageKey(data, "privacy"))}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default SlaStatisticsSurface;
